import { connect } from 'react-redux';
import { formValueSelector, reduxForm } from 'redux-form';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { NodeT } from 'fable/flowEditor';

import { createConditionModal } from '../components/modals';
import type {
  ConditionModalDispatchPropsT,
  ConditionModalOwnPropsT,
  ConditionModalPropsT,
  ConditionModalStatePropsT,
} from '../components/modals';
import {
  MatchingOperator,
  MatchingStrategy,
  MatchingSystemType,
  VariableScope,
} from '../components/nodeTypes/types';
import type {
  ConditionFormValuesT,
  MatchingRuleT,
} from '../components/nodeTypes/types';

const FORM_NAME = 'automationInitForm';
export const DEFAULT_MATCHING_STRATEGY = MatchingStrategy.ANY;
const validate = () => {
  return {};
};

const valueSelect = formValueSelector(FORM_NAME);

const mapStateToProps =
  <TNode extends NodeT>() =>
  (
    state: RootReducerStateT,
    props: ConditionModalOwnPropsT<TNode>,
  ): ConditionModalStatePropsT => {
    const { node } = props;
    const initialValues: ConditionFormValuesT = {
      matchingStrategy: DEFAULT_MATCHING_STRATEGY,
      rules: [
        {
          variable: {
            scope: VariableScope.SYSTEM,
            variableType: MatchingSystemType.QUEUE,
          },
          operator: MatchingOperator.EQUALS,
          match: {
            scope: VariableScope.INPUT,
            values: [],
          },
        },
      ],
    };

    if (node.payload !== null) {
      if (node.payload.matchingStrategy) {
        initialValues.matchingStrategy = node.payload.matchingStrategy;
      }

      if (node.payload.rules) {
        initialValues.rules = node.payload.rules.map((rule: MatchingRuleT) => {
          return {
            variable: rule.variable,
            operator: rule.operator || MatchingOperator.EQUALS,
            match: rule.match,
          };
        });
      }
    }

    return {
      initialValues,
      matchingStrategy:
        (valueSelect(
          state,
          'matchingStrategy',
        ) as ConditionFormValuesT['matchingStrategy']) ||
        initialValues.matchingStrategy,
      rules:
        (valueSelect(state, 'rules') as ConditionFormValuesT['rules']) ||
        initialValues.rules,
    };
  };

export const createConditionModalContainer = <TNode extends NodeT>(
  type: TNode['nodeType'],
  createInitialPayload: (
    node: ConditionModalPropsT<TNode>['node'],
  ) => TNode['payload'],
) => {
  const ConditionalModal = createConditionModal<TNode>(
    type,
    createInitialPayload,
  );

  return connect<
    ConditionModalStatePropsT,
    ConditionModalDispatchPropsT,
    ConditionModalOwnPropsT<TNode>,
    RootReducerStateT
  >(mapStateToProps<TNode>())(
    reduxForm<ConditionFormValuesT, ConditionModalPropsT<TNode>>({
      form: FORM_NAME, // A unique identifier for this form.
      enableReinitialize: true,
      validate,
    })(ConditionalModal),
  );
};
